import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { DateTime as d } from "luxon";
import { ApolloError } from "apollo-boost";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SimpleDateTimePicker } from "../../../SimpleDateTimePicker/intex";
import { CONSUMER_REQUEST_BOOKING_EXTENSION } from "../../../../graphql/bookings/consumerRequestBookingExtensionMutation";
import { CONSUMER_CREATE_PAYMENT_FOR_EXTENSION } from "../../../../graphql/bookings/consumerCreatePaymentForExtensionMutation";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage, toCurrency } from "../../../utils";
import { IBooking } from "../../../../reducers/bookings/types";
import { IAppState } from "../../../../store";
import StripePayment from "../../../BookingConfirmation/StripePayment";
import { loadConvergeUS } from "../../../../utils/converge";

interface IDateTime {
  date: string;
  time: number;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  booking: IBooking;
  onExtended: () => void;
  timeZone?: string;
}

interface IExtensionResult {
  id: string;
  referenceNumber: string;
  status: string;
  endDate: string;
  conflictingBookingReference?: string;
  vehicleAvailableUntil?: string;
  // NU-13057: only set when this extension is confirmable (vehicle available)
  // but requires payment first - presence of this field is what distinguishes
  // "pending payment" from "pending staff review due to a conflict", both of
  // which otherwise show status === "QUOTE".
  quotePaymentExpiresAt?: string;
  paymentDetails?: {
    totalPayableAmount: number;
  };
}

// NU-12537: self-service extension request. Mirrors the shape of
// GenerateSwapRequestDialog.tsx (also a customer request against their own
// active booking) but is deliberately much simpler - an extension only ever
// needs a new end date, no vehicle re-allocation UI.
// NU-13057: extended with a payment step - when the organisation requires
// payment for self-service extensions (not a business-customer/invoiced
// account, not a zero-cost/courtesy/test-drive booking), a vehicle-available
// result comes back as a pending QUOTE with quotePaymentExpiresAt set, and
// the customer must pay before the extension is actually confirmed.
export const ExtendBookingDialog: React.FC<IProps> = ({ open, onClose, booking, onExtended, timeZone }) => {
  const { t } = useTranslation();
  const snackbar = useSnackBar();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { currency, stripeAccountId, locale } = website.organisation;
  const currentEndDate = booking.dropoffDateTime || "";
  const bookingBranch = website.branches?.find((branch) => branch.id === booking.branchId);
  const maxExtensionEndDate =
    booking.pickupDateTime &&
    bookingBranch?.maxBookingDurationOptIn &&
    bookingBranch.maxBookingDuration
      ? d.fromISO(booking.pickupDateTime).plus({ days: bookingBranch.maxBookingDuration }).toUTC().toISO()
      : undefined;
  const [newEndDate, setNewEndDate] = useState<string>(currentEndDate);
  const [result, setResult] = useState<IExtensionResult | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripePaymentIntentSecret, setStripePaymentIntentSecret] = useState<string>("");

  const customerId = user?.businesscustomers ? undefined : user?.customerId;
  const businessCustomerId = user?.businesscustomers?.id;

  const [requestExtension, { loading }] = useMutation(CONSUMER_REQUEST_BOOKING_EXTENSION, {
    onCompleted: (data) => {
      setResult(data.consumerRequestBookingExtension);
      if (data.consumerRequestBookingExtension?.status === "CONFIRMED") {
        onExtended();
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    },
  });

  const [createPaymentForExtension] = useMutation(CONSUMER_CREATE_PAYMENT_FOR_EXTENSION, {
    onError: (error: ApolloError) => {
      setPaymentLoading(false);
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    },
    onCompleted: (data) => {
      const payment = data?.consumerCreatePaymentForExtension?.payment;
      if (!data?.consumerCreatePaymentForExtension?.success || !payment) {
        setPaymentLoading(false);
        return;
      }
      if (payment.stripePaymentIntentSecret) {
        setPaymentLoading(false);
        setStripePaymentIntentSecret(payment.stripePaymentIntentSecret);
      } else if (payment.convergePaymentSessionUrl) {
        window.location = payment.convergePaymentSessionUrl;
      } else if (payment.convergeUSSessionToken) {
        loadConvergeUS({
          paymentSessionToken: payment.convergeUSSessionToken,
          onloadFunction() {
            handleClose();
          },
          redirectFunction() {
            onExtended();
          }
        });
      } else if (payment.gpTransactionUrl) {
        window.location = payment.gpTransactionUrl;
      } else {
        // Offline/zero-amount gateway - createPaymentForExtension already
        // confirmed the extension synchronously (see payment.ts).
        setPaymentLoading(false);
        onExtended();
      }
    }
  });

  const handleClose = () => {
    setResult(null);
    setNewEndDate(currentEndDate);
    setStripePaymentIntentSecret("");
    setPaymentLoading(false);
    onClose();
  };

  const handleSubmit = () => {
    requestExtension({
      variables: {
        bookingId: booking.id,
        endDate: newEndDate,
        customerId,
        businessCustomerId
      }
    });
  };

  const handlePayForExtension = () => {
    if (!result) {
      return;
    }
    setPaymentLoading(true);
    createPaymentForExtension({
      variables: {
        payment: {
          bookingExtensionId: result.id,
          currency,
          successUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
          cancelUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
        },
        customerId,
        businessCustomerId
      }
    });
  };

  const paymentRequired = !!(result?.status === "QUOTE" && result.quotePaymentExpiresAt);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("root:extendBooking")}</DialogTitle>
      <DialogContent>
        {!result && (
          <>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              {t("root:extendBookingDescription")}
            </Typography>
            <SimpleDateTimePicker
              date={newEndDate}
              dateTitle={t("root:newReturnDate")}
              timeTitle={t("root:newReturnTime")}
              name="extensionEndDate"
              minDate={currentEndDate}
              maxDate={maxExtensionEndDate}
              timeZone={timeZone}
              handleChange={(date: IDateTime) => setNewEndDate(date.date)}
            />
          </>
        )}
        {result && result.status === "CONFIRMED" && (
          <Typography variant="body1">
            {t("root:extensionConfirmedMessage", {
              date: d.fromISO(result.endDate).toFormat("dd LLL yyyy, hh:mm a"),
            })}
          </Typography>
        )}
        {result && result.status === "QUOTE" && !paymentRequired && (
          <Typography variant="body1">
            {result.vehicleAvailableUntil
              ? t("root:extensionConflictMessageWithBoundary", {
                  date: d.fromISO(result.vehicleAvailableUntil).toFormat("dd LLL yyyy, hh:mm a"),
                })
              : t("root:extensionConflictMessage")}
          </Typography>
        )}
        {paymentRequired && stripePaymentIntentSecret && (
          <StripePayment
            stripePaymentIntentSecret={stripePaymentIntentSecret}
            bookingId={booking.id}
            returnUrl={`account/bookings/${booking.id}`}
          />
        )}
        {paymentRequired && !stripePaymentIntentSecret && (
          <>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              {t("root:extensionPaymentRequiredDescription")}
            </Typography>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Typography variant="h4">{t("root:totalPayableAmount")}:</Typography>
              <Typography variant="h3" className="bold text-accent">
                {toCurrency(result?.paymentDetails?.totalPayableAmount || 0, currency, locale)}
              </Typography>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!result && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {t("misc:cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading || !newEndDate || newEndDate <= currentEndDate}
            >
              {loading ? <CircularProgress size={20} /> : t("root:requestExtension")}
            </Button>
          </>
        )}
        {result && !paymentRequired && (
          <Button onClick={handleClose} color="primary" variant="contained">
            {t("misc:close")}
          </Button>
        )}
        {paymentRequired && !stripePaymentIntentSecret && (
          <>
            <Button onClick={handleClose} disabled={paymentLoading}>
              {t("misc:cancel")}
            </Button>
            <Button
              onClick={handlePayForExtension}
              color="primary"
              variant="contained"
              disabled={paymentLoading}
            >
              {paymentLoading ? <CircularProgress size={20} /> : t("root:payNow")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExtendBookingDialog;
